import type { AssetId, LabelAssociation, LabelId } from "@storytell/types";
import { batch } from "solid-js";
import type { AssetsService } from "../assets";
import type { ConceptsService } from "../concepts/conceptsService";
import { buildRelationshipsCache } from "./relationshipsCache";

export type RelsService = ReturnType<typeof createRelsService>;

export const createRelsService = (deps: {
  assets: AssetsService;
  concepts: ConceptsService;
  /** Used when rebuilding rels so label–asset links match associations even if assets lack `labels`. */
  latestLabelAssociations?: () => readonly LabelAssociation[];
}) => {
  const cache = buildRelationshipsCache();

  const rebuildFullCache: typeof cache.rebuildFullCache = (input) => {
    const extra = deps.latestLabelAssociations?.() ?? [];
    cache.rebuildFullCache({
      ...input,
      assetLabelAssociations: extra,
    });
  };

  const addLabelToAsset = (assetId: AssetId, labelId: LabelId) => {
    const asset = deps.assets.get(assetId);
    if (!asset) {
      return;
    }
    batch(() => {
      deps.assets.set(assetId, {
        ...asset,
        labels: Array.from(new Set([...(asset.labels ?? []), labelId])),
      });
      cache.assetLabelAdded(assetId, labelId, deps.concepts.all());
    });
  };
  const removeLabelFromAsset = (assetId: AssetId, labelId: LabelId) => {
    const asset = deps.assets.get(assetId);
    if (!asset) {
      return;
    }
    batch(() => {
      deps.assets.set(assetId, {
        ...asset,
        labels: (asset.labels ?? []).filter((l) => l !== labelId),
      });
      cache.assetLabelDeleted(assetId, labelId, deps.concepts.all());
    });
  };

  const onAssetDeleted = (assetId: AssetId) => {
    batch(() => {
      deps.assets.remove(assetId);
      const conceptsWithSeedsRemoved = deps.concepts.all().map((c) => ({
        ...c,
        seeds: c.seeds?.filter((s) => s.assetId !== assetId),
      }));
      cache.assetDeleted(assetId, conceptsWithSeedsRemoved);
      const conceptsWithUpdatedLabels = conceptsWithSeedsRemoved
        .filter((c) => !c.seeds || c.seeds.length === 0)
        .map((c) => ({
          ...c,
          labels: cache.labelsForConcept(c.id),
        }));
      deps.concepts.hydrateFromSync(conceptsWithUpdatedLabels);
    });
  };
  return { ...cache, rebuildFullCache, addLabelToAsset, removeLabelFromAsset, onAssetDeleted };
};
